import React, { createContext, useContext, useState } from "react";
import { EXTRA_PATHS, ROLE_PATHS } from "../../config/config";
import { soulManager } from "../managers/SoulManager";
import { SoulDataModel } from "../models/Soul/SoulDataModel";

export interface UserDataContextType {
  data?: SoulDataModel;
  permittedPaths: string[];
  loading: boolean;
  hasError: boolean;
  reloadUserData: () => Promise<boolean>;
  clearUserData: () => void;
}

const UserDataContext = createContext<UserDataContextType | undefined>(
  undefined
);

export const UserDataProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [userData, setUserData] = useState<SoulDataModel>();
  const [permittedPaths, setPermittedPaths] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  function clearUserData() {
    setUserData(undefined);
    setPermittedPaths([]);
    setHasError(false);
    setLoading(false);
  }

  async function getUserData(): Promise<boolean> {
    setLoading(true);
    setHasError(false);
    try {
      const data = await soulManager.getSoul();
      if (!data?.userId) {
        setUserData(undefined);
        setPermittedPaths([]);
        setHasError(true);
        return false;
      }

      setUserData(data);
      const role = (data.role?.toUpperCase() || "USER") as keyof typeof ROLE_PATHS;
      const currentPermittedPaths = ROLE_PATHS[role] ?? [];
      let extraPaths: string[] = [];
      if (role === "USER" && data.isParentAccount) {
        extraPaths = EXTRA_PATHS[role] ?? [];
      }
      setPermittedPaths([...currentPermittedPaths, ...extraPaths]);
      return true;
    } catch (error) {
      console.error("Error cargando datos de usuario:", error);
      setUserData(undefined);
      setPermittedPaths([]);
      setHasError(true);
      return false;
    } finally {
      setLoading(false);
    }
  }

  const value: UserDataContextType = {
    data: userData,
    permittedPaths,
    loading,
    hasError,
    reloadUserData: getUserData,
    clearUserData,
  };

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
};

export function useUserData() {
  return useContext(UserDataContext);
}
